import type { SvgIconName } from '@components/Svg';
import type { User } from '@domain/schemas/database.schema';
import { UserRole } from '@domain/schemas/user-role';
import type { BaseUser } from '@helpers/auth/types';
import type { FeatureFlag } from '@root/helpers/feature-flags';
import { getUserRole } from '@root/helpers/permissions/utils';

import Nav, { config } from '../routes';

type Menu = {
  path: Nav;
  icon: SvgIconName;
  allowedRoles?: string[];
  featureFlag?: FeatureFlag;
  group?: 'working' | 'support';
};

export type MenuNavigation = Pick<Menu, 'path' | 'icon' | 'group'> & {
  name: string;
};

export const getMenu = (): Menu[] => [
  {
    path: Nav.Submissions,
    icon: 'submissions',
    allowedRoles: [UserRole.Admin, UserRole.Superadmin, UserRole.User],
    group: 'working',
  },
  {
    path: Nav.Templates,
    icon: 'templates',
    allowedRoles: [UserRole.Admin, UserRole.Superadmin, UserRole.TemplateManager],
    group: 'working',
  },
  {
    path: Nav.Blocks,
    icon: 'blocks',
    allowedRoles: [UserRole.Admin, UserRole.Superadmin, UserRole.TemplateManager],
    group: 'working',
  },
  {
    path: Nav.Clauses,
    icon: 'clauses',
    allowedRoles: [UserRole.Admin, UserRole.Superadmin, UserRole.TemplateManager],
    group: 'working',
  },
  {
    path: Nav.Branding,
    icon: 'photo',
    featureFlag: 'Branding',
    allowedRoles: [UserRole.Admin, UserRole.Superadmin, UserRole.TemplateManager],
    group: 'support',
  },
  {
    path: Nav.Tags,
    icon: 'tags',
    allowedRoles: [UserRole.Admin, UserRole.Superadmin, UserRole.TemplateManager],
    group: 'support',
  },
  { path: Nav.Teams, icon: 'teams', allowedRoles: [UserRole.Superadmin], featureFlag: 'Teams', group: 'support' },
  { path: Nav.Users, icon: 'user-card', allowedRoles: [UserRole.Superadmin], featureFlag: 'Users', group: 'support' },
  {
    path: Nav.EvalConfigs,
    icon: 'settings',
    allowedRoles: [UserRole.Superadmin],
    featureFlag: 'EvalConfigs',
    group: 'support',
  },
];

export const createMenuForUser = (
  user: User | BaseUser | null,
  getFeatureFlagState: (featureFlag: FeatureFlag) => boolean | undefined,
): MenuNavigation[] => {
  if (!user) {
    return [];
  }

  const userRole = getUserRole(user);

  if (!userRole) {
    return [];
  }

  const menu = getMenu();

  const filtered = menu.filter(({ allowedRoles, featureFlag }) => {
    if (featureFlag && !getFeatureFlagState(featureFlag)) {
      return false;
    }

    if (!allowedRoles) {
      return true;
    }

    return allowedRoles.includes(userRole);
  });

  return filtered.map(({ path, icon, group }) => ({ path, icon, group, name: config[path].title }));
};
